/**
 * 设计文档管理器
 * 使用模板生成和管理设计文档，完善三文档体系
 */

import fs from 'fs-extra';
import path from 'path';
import { logger } from '../utils/logger';
import Handlebars from 'handlebars';
import { Task, TaskStatus } from '../types';
import { TaskStore } from './task-store';

export interface DesignDocOptions {
  template?: 'full' | 'simple' | string; // 模板选择
  title: string;
  description: string;
  author?: string;
  version?: string;
  prdId?: string; // 关联的 PRD ID
  architecture?: {
    frontend?: string;
    backend?: string;
    database?: string;
    cache?: string;
  };
  modules?: Array<{
    name: string;
    description: string;
    interfaces?: string[];
  }>;
  tasks?: Array<{
    name: string;
    description: string;
    estimatedHours?: number;
  }>;
}

export interface DesignDoc {
  id: string;
  title: string;
  path: string;
  prdId?: string;
  createdAt: Date;
  updatedAt: Date;
  status: 'draft' | 'review' | 'approved' | 'archived';
  taskIds?: string[]; // 关联的任务 ID
}

export class DesignDocManager {
  private templateDir: string;
  private docsDir: string;
  private indexPath: string;
  private taskStore: TaskStore;
  
  constructor(workflowDir?: string) {
    const projectRoot = workflowDir || process.cwd();
    this.templateDir = path.join(projectRoot, '.workflow', 'templates', 'design');
    this.docsDir = path.join(projectRoot, '.workflow', 'docs', 'design');
    this.indexPath = path.join(this.docsDir, 'index.json');
    this.taskStore = new TaskStore(projectRoot);
  }
  
  /**
   * 从 PRD 生成设计文档
   */
  async generateFromPRD(prdPath: string, options: Partial<DesignDocOptions> = {}): Promise<{ doc: DesignDoc; content: string }> {
    logger.info('Generating design document from PRD:', prdPath);
    
    // 读取 PRD 内容
    const prdContent = await fs.readFile(prdPath, 'utf-8');
    
    // 解析 PRD 提取关键信息
    const parsed = this.parsePRD(prdContent);
    
    // 合并选项
    const finalOptions: DesignDocOptions = {
      template: options.template || 'simple',
      title: options.title || parsed.title + ' - 技术设计',
      description: options.description || parsed.description,
      author: options.author || '@architect',
      version: options.version || 'v0.1.0',
      prdId: options.prdId || path.basename(prdPath, '.md'),
      architecture: options.architecture || parsed.suggestedArchitecture,
      modules: options.modules || parsed.suggestedModules,
      tasks: options.tasks || parsed.suggestedTasks,
    };
    
    // 生成设计文档
    return this.generate(finalOptions);
  }
  
  /**
   * 使用模板生成设计文档
   */
  async generate(options: DesignDocOptions): Promise<{ doc: DesignDoc; content: string }> {
    // 确保目录存在
    await fs.ensureDir(this.docsDir);
    
    // 选择模板
    const templatePath = await this.getTemplatePath(options.template || 'simple');
    
    // 读取模板
    const templateContent = await fs.readFile(templatePath, 'utf-8');
    
    // 准备数据
    const data = this.prepareTemplateData(options);
    
    // 编译模板
    const template = Handlebars.compile(templateContent);
    const content = template(data);
    
    // 保存设计文档
    const docId = `design-${Date.now()}`;
    const fileName = `${options.title.replace(/[^a-zA-Z0-9\u4e00-\u9fa5]/g, '-')}.md`;
    const docPath = path.join(this.docsDir, fileName);
    
    await fs.writeFile(docPath, content);
    
    // 创建设计文档记录
    const doc: DesignDoc = {
      id: docId,
      title: options.title,
      path: docPath,
      prdId: options.prdId,
      createdAt: new Date(),
      updatedAt: new Date(),
      status: 'draft',
      taskIds: [],
    };
    
    // 更新索引
    await this.updateIndex(doc);
    
    // 如果有任务建议，自动创建任务
    if (options.tasks && options.tasks.length > 0) {
      const taskIds = await this.createTasksFromDesign(options.tasks, doc.id);
      doc.taskIds = taskIds;
      await this.updateIndex(doc);
    }
    
    logger.info(`Design document created: ${docPath}`);
    
    return { doc, content };
  }
  
  /**
   * 准备模板数据
   */
  private prepareTemplateData(options: DesignDocOptions): any {
    const isSimple = options.template === 'simple';
    
    const baseData = {
      ...options,
      date: new Date().toISOString().split('T')[0],
      系统名称: options.title,
      设计概述: options.description,
      version: options.version,
      author: options.author,
      prdId: options.prdId || '无',
    };
    
    if (isSimple) {
      // 简化版模板数据
      return {
        ...baseData,
        设计目标描述: options.description,
        目标1: '实现核心功能',
        目标2: '保证系统性能',
        目标3: '确保可扩展性',
        
        // 技术栈
        前端技术: options.architecture?.frontend || 'React + TypeScript',
        后端技术: options.architecture?.backend || 'Node.js + Express',
        数据库技术: options.architecture?.database || 'PostgreSQL',
        缓存技术: options.architecture?.cache || 'Redis',
        
        // 模块
        模块1: options.modules?.[0]?.name || '核心业务模块',
        功能描述1: options.modules?.[0]?.description || '处理核心业务逻辑',
        接口1: options.modules?.[0]?.interfaces?.[0] || 'createResource',
        接口说明1: '创建资源',
        接口2: options.modules?.[0]?.interfaces?.[1] || 'getResource',
        接口说明2: '获取资源',
        
        模块2: options.modules?.[1]?.name || '数据管理模块',
        功能描述2: options.modules?.[1]?.description || '管理数据存储和查询',
        数据结构名: 'Resource',
        字段1: 'id',
        类型1: 'string',
        字段2: 'name',
        类型2: 'string',
        
        // 流程
        流程名称: '核心业务流程',
        步骤1: '接收用户请求',
        步骤2: '验证请求参数',
        步骤3: '执行业务逻辑',
        步骤4: '返回处理结果',
        
        // 任务
        任务1: options.tasks?.[0]?.name || '搭建项目基础架构',
        任务说明1: options.tasks?.[0]?.description || '初始化项目结构和配置',
        工时1: options.tasks?.[0]?.estimatedHours ? `${options.tasks[0].estimatedHours}h` : '8h',
        
        任务2: options.tasks?.[1]?.name || '实现核心功能模块',
        任务说明2: options.tasks?.[1]?.description || '开发主要业务逻辑',
        工时2: options.tasks?.[1]?.estimatedHours ? `${options.tasks[1].estimatedHours}h` : '16h',
        
        任务3: options.tasks?.[2]?.name || '数据库设计与实现',
        任务说明3: options.tasks?.[2]?.description || '设计数据模型和实现数据访问层',
        工时3: options.tasks?.[2]?.estimatedHours ? `${options.tasks[2].estimatedHours}h` : '8h',
        
        任务4: options.tasks?.[3]?.name || '接口开发与测试',
        任务说明4: options.tasks?.[3]?.description || '实现 API 接口并编写测试',
        工时4: options.tasks?.[3]?.estimatedHours ? `${options.tasks[3].estimatedHours}h` : '12h',
        
        // 注意事项
        注意事项1: '确保代码质量和测试覆盖率',
        注意事项2: '遵循项目编码规范',
        注意事项3: '及时更新文档',
      };
    } else {
      // 完整版模板数据
      return {
        ...baseData,
        // 添加完整版特有的字段
        原则1: '可扩展性优先',
        原则2: '性能与体验平衡',
        原则3: '安全性保障',
        
        约束1: '必须兼容现有系统',
        约束2: '响应时间不超过 200ms',
        约束3: '支持水平扩展',
        
        // 更多详细配置...
        // 这里可以根据需要添加更多字段
      };
    }
  }
  
  /**
   * 解析 PRD 内容
   */
  private parsePRD(prdContent: string): {
    title: string;
    description: string;
    suggestedArchitecture: any;
    suggestedModules: any[];
    suggestedTasks: any[];
  } {
    // 简单的解析逻辑，提取标题和描述
    const lines = prdContent.split('\n');
    let title = '';
    let description = '';
    
    for (const line of lines) {
      if (line.startsWith('# ') && !title) {
        title = line.substring(2).trim();
      } else if (line.includes('背景') || line.includes('描述')) {
        const nextLineIndex = lines.indexOf(line) + 1;
        if (nextLineIndex < lines.length) {
          description = lines[nextLineIndex].trim();
        }
      }
    }
    
    // 基于 PRD 内容推荐架构和模块
    const suggestedArchitecture = {
      frontend: 'React + TypeScript',
      backend: 'Node.js + Express',
      database: 'PostgreSQL',
      cache: 'Redis',
    };
    
    const suggestedModules = [
      {
        name: '用户管理模块',
        description: '处理用户认证和授权',
        interfaces: ['login', 'logout', 'register', 'updateProfile'],
      },
      {
        name: '业务核心模块',
        description: '实现主要业务功能',
        interfaces: ['create', 'read', 'update', 'delete'],
      },
    ];
    
    const suggestedTasks = [
      {
        name: '项目初始化',
        description: '搭建项目基础结构',
        estimatedHours: 4,
      },
      {
        name: '数据库设计',
        description: '设计数据模型和表结构',
        estimatedHours: 8,
      },
      {
        name: 'API 开发',
        description: '实现后端接口',
        estimatedHours: 16,
      },
      {
        name: '前端开发',
        description: '实现用户界面',
        estimatedHours: 24,
      },
    ];
    
    return {
      title: title || '未命名项目',
      description: description || '项目描述',
      suggestedArchitecture,
      suggestedModules,
      suggestedTasks,
    };
  }
  
  /**
   * 从设计文档创建任务
   */
  private async createTasksFromDesign(tasks: Array<{ name: string; description: string; estimatedHours?: number }>, designDocId: string): Promise<string[]> {
    await this.taskStore.initialize();
    const taskIds: string[] = [];
    
    for (const taskDef of tasks) {
      const task = await this.taskStore.addTask({
        content: taskDef.name,
        notes: taskDef.description,
        status: 'todo' as TaskStatus,
        priority: 'medium',
        estimatedHours: taskDef.estimatedHours,
        tags: ['design', designDocId],
        section: '技术设计任务',
      });
      
      taskIds.push(task.id);
    }
    
    logger.info(`Created ${taskIds.length} tasks from design document`);
    return taskIds;
  }
  
  /**
   * 获取模板路径
   */
  private async getTemplatePath(template: string): Promise<string> {
    // 确保模板目录存在
    await fs.ensureDir(this.templateDir);
    
    // 如果是预定义模板
    if (template === 'full') {
      return path.join(this.templateDir, 'template.md');
    } else if (template === 'simple') {
      return path.join(this.templateDir, 'simple-template.md');
    }
    
    // 自定义模板路径
    const customPath = path.resolve(template);
    if (await fs.pathExists(customPath)) {
      return customPath;
    }
    
    // 在模板目录中查找
    const templatePath = path.join(this.templateDir, template);
    if (await fs.pathExists(templatePath)) {
      return templatePath;
    }
    
    // 默认使用简单模板
    logger.warn(`Template not found: ${template}, using simple template`);
    return path.join(this.templateDir, 'simple-template.md');
  }
  
  /**
   * 更新设计文档索引
   */
  private async updateIndex(doc: DesignDoc): Promise<void> {
    let index: DesignDoc[] = [];
    
    if (await fs.pathExists(this.indexPath)) {
      index = await fs.readJson(this.indexPath);
    }
    
    // 查找是否已存在
    const existingIndex = index.findIndex(d => d.id === doc.id);
    if (existingIndex >= 0) {
      index[existingIndex] = doc;
    } else {
      index.push(doc);
    }
    
    await fs.writeJson(this.indexPath, index, { spaces: 2 });
  }
  
  /**
   * 获取所有设计文档
   */
  async listDesignDocs(): Promise<DesignDoc[]> {
    if (!await fs.pathExists(this.indexPath)) {
      return [];
    }
    
    return fs.readJson(this.indexPath);
  }
  
  /**
   * 获取设计文档内容
   */
  async getDesignDoc(id: string): Promise<{ doc: DesignDoc; content: string } | null> {
    const docs = await this.listDesignDocs();
    const doc = docs.find(d => d.id === id);
    
    if (!doc) {
      return null;
    }
    
    const content = await fs.readFile(doc.path, 'utf-8');
    return { doc, content };
  }
  
  /**
   * 更新设计文档状态
   */
  async updateStatus(id: string, status: DesignDoc['status']): Promise<boolean> {
    const docs = await this.listDesignDocs();
    const doc = docs.find(d => d.id === id);
    
    if (!doc) {
      return false;
    }
    
    doc.status = status;
    doc.updatedAt = new Date();
    
    await this.updateIndex(doc);
    return true;
  }
  
  /**
   * 关联任务到设计文档
   */
  async linkTasks(designDocId: string, taskIds: string[]): Promise<boolean> {
    const docs = await this.listDesignDocs();
    const doc = docs.find(d => d.id === designDocId);
    
    if (!doc) {
      return false;
    }
    
    doc.taskIds = [...new Set([...(doc.taskIds || []), ...taskIds])];
    doc.updatedAt = new Date();
    
    await this.updateIndex(doc);
    return true;
  }
  
  /**
   * 列出可用的模板
   */
  async listTemplates(): Promise<string[]> {
    if (!await fs.pathExists(this.templateDir)) {
      return [];
    }
    
    const files = await fs.readdir(this.templateDir);
    return files.filter(f => f.endsWith('.md'));
  }
}